import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import { isCPFOrCNPJ } from 'brazilian-values';
import { FormikProvider, useFormik } from 'formik';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';
import * as yup from 'yup';

import { useMutationCallbacks } from '@woovi/relay';
import { PAYMENT_METHOD_TYPE } from '@woovi/shared';
import { ActionButton } from '@woovi/ui';

import type { PaymentActiveWaitingPaymentMethodPixCreditCard_charge$key } from './__generated__/PaymentActiveWaitingPaymentMethodPixCreditCard_charge.graphql.ts';
import type { PaymentActiveWaitingPaymentMethodPixCreditCard_query$key } from './__generated__/PaymentActiveWaitingPaymentMethodPixCreditCard_query.graphql.ts';
import PaymentPixCreditOptions from './PaymentPixCreditOptions.tsx';
import { useGraphQLType } from '../../../graphql/useGraphQLType.ts';
import PaymentActiveCreditCard from '../../active/creditCard/PaymentActiveCreditCard.tsx';
import PaymentLinkAdditionalInfo from '../../paymentLink/PaymentLinkAdditionalInfo.tsx';
import PaymentLinkComment from '../../paymentLink/PaymentLinkComment.tsx';
import PaymentLinkPaymentTerm from '../../paymentLink/PaymentLinkPaymentTerm.tsx';
import PaymentLinkTransactionDetails from '../../PaymentLinkTransactionDetails.tsx';
import { PaymentActiveWaitingPaymentMethodCreditCardFormFields } from '../creditCard/PaymentActiveWaitingPaymentMethodCreditCardFormFields.tsx';
import type { ChargeAddPaymentMethodMutation } from '../mutations/__generated__/ChargeAddPaymentMethodMutation.graphql.ts';
import { ChargeAddPaymentMethod } from '../mutations/ChargeAddPaymentMethodMutation.tsx';
import PaymentActiveCustomer from '../PaymentActiveCustomer.tsx';

type PaymentActiveWaitingPaymentMethodPixCreditCardValues = {
  holderName: string;
  holderTaxID: string;
  number: string;
  expireDate: string;
  cvv: string;
};

type PaymentActiveWaitingPaymentMethodPixCreditCardProps = {
  query: PaymentActiveWaitingPaymentMethodPixCreditCard_query$key;
  charge: PaymentActiveWaitingPaymentMethodPixCreditCard_charge$key;
};

const PaymentActiveWaitingPaymentMethodPixCreditCard = (
  props: PaymentActiveWaitingPaymentMethodPixCreditCardProps,
) => {
  const { t } = useTranslation();

  const step = {
    options: 'options',
    creditCard: 'creditCard',
  };

  const [activeStep, setActiveStep] = useState<string>(step.options);
  const [pixCreditOption, setPixCreditOption] = useState<string>('0');

  const query =
    useFragment<PaymentActiveWaitingPaymentMethodPixCreditCard_query$key>(
      graphql`
        fragment PaymentActiveWaitingPaymentMethodPixCreditCard_query on Query {
          appEnv
          ...useGraphQLType_query
        }
      `,
      props.query,
    );

  const charge =
    useFragment<PaymentActiveWaitingPaymentMethodPixCreditCard_charge$key>(
      graphql`
        fragment PaymentActiveWaitingPaymentMethodPixCreditCard_charge on Charge {
          id
          status
          identifier
          availablePaymentMethods
          company {
            nameFriendly
            taxID {
              taxID
            }
          }
          customer {
            name
            taxID {
              taxID
            }
          }
          availablePaymentMethodsPixCredit {
            options {
              valueInCash
              installmentNumber
              installmentValue
              installmentValueTotal
            }
          }
          valueWithDiscount
          ...PaymentPixCreditOptions_charge
          ...PaymentActiveCreditCard_charge
          ...PaymentLinkComment_charge
          ...PaymentActiveCustomer_charge
          ...PaymentLinkAdditionalInfo_charge
          ...PaymentLinkPaymentTerm_charge
          ...PaymentLinkTransactionDetails_charge
        }
      `,
      props.charge,
    );

  const { isGraphQLMain } = useGraphQLType(query);
  const isPixInCash = (optionSelected: string) =>
    parseInt(optionSelected, 10) === 0;

  const [chargeAddPaymentMethod, isPendingChargeAddPaymentMethod] =
    useMutationCallbacks<ChargeAddPaymentMethodMutation>({
      name: 'ChargeAddPaymentMethod',
      mutation: ChargeAddPaymentMethod,
    });

  const handleChargeAddPaymentMethod = (
    values: PaymentActiveWaitingPaymentMethodPixCreditCardValues,
  ) => {
    if (!pixCreditOption) {
      return;
    }

    const getInput = () => {
      if (isPixInCash(pixCreditOption)) {
        // @todo calculate discount for pix in cash option
        return {
          chargeId: charge.id,
          paymentMethodTypes: [PAYMENT_METHOD_TYPE.PIX_COB],
          paymentMethodPixCobConfig: {
            value: charge.valueWithDiscount,
          },
        };
      }

      const expMonth = values.expireDate.split('/')[0];
      const expYear = values.expireDate.split('/')[1];

      const card = {
        holderName: values.holderName,
        holderTaxID: values.holderTaxID,
        number: values.number,
        expMonth: parseInt(expMonth, 10),
        expYear: parseInt(expYear, 10),
        cvv: values.cvv,
      };

      const { availablePaymentMethodsPixCredit } = charge;

      const { valueInCash, installmentNumber, installmentValueTotal } =
        availablePaymentMethodsPixCredit.options.find(
          (option) =>
            option.installmentNumber === parseInt(pixCreditOption, 10),
        );

      return {
        chargeId: charge.id,
        paymentMethodTypes: [
          PAYMENT_METHOD_TYPE.PIX_COB,
          PAYMENT_METHOD_TYPE.CREDIT_CARD,
        ],
        paymentMethodPixCobConfig: {
          value: valueInCash,
        },
        paymentMethodCreditCardConfig: {
          value: installmentValueTotal,
          installmentNumber: parseInt(installmentNumber, 10),
          card,
        },
      };
    };

    const input = getInput();

    const config = {
      variables: {
        input,
      },
    };

    chargeAddPaymentMethod(config);
  };

  const initialValues = useMemo(
    () => ({
      holderName: '',
      holderTaxID: charge.customer.taxID.taxID,
      number: '',
      expireDate: '',
      cvv: '',
    }),
    [],
  );

  const validationSchema = yup.object().shape({
    holderName: yup
      .string()
      .test('holderName', t('Please enter your full name'), (value) => {
        if (!value) {
          return false;
        }

        const names = value.split(' ');

        return names.length > 1;
      })
      .required(t('Name is required')),
    holderTaxID: yup
      .string()
      .test('isCPF', t('Customer TaxID is invalid'), (value) => {
        if (!value) {
          return true;
        }

        return isCPFOrCNPJ(value);
      })
      .required(t('TaxID is required')),
    number: yup.string().required(t('Card expiration is mandatory')),
    expireDate: yup.string().required(t('Card expiration is mandatory')),
    cvv: yup.string().required(t('Card code is required')),
  });

  const formikbag =
    useFormik<PaymentActiveWaitingPaymentMethodPixCreditCardValues>({
      validateOnBlur: true,
      validateOnMount: false,
      enableReinitialize: true,
      initialValues,
      validationSchema,
      onSubmit: handleChargeAddPaymentMethod,
    });

  const { isValid, handleSubmit } = formikbag;

  const renderStep = () => {
    if (activeStep === step.creditCard) {
      return (
        <Stack>
          <PaymentActiveWaitingPaymentMethodCreditCardFormFields />
          <ActionButton
            disabled={
              !pixCreditOption || !isValid || isPendingChargeAddPaymentMethod
            }
            onClick={handleSubmit}
            loading={isPendingChargeAddPaymentMethod}
          >
            {t('Pay')}
          </ActionButton>
        </Stack>
      );
    }

    const handleSetStep = async () => {
      if (parseInt(pixCreditOption, 10) === 0) {
        return handleChargeAddPaymentMethod({});
      }

      setActiveStep(step.creditCard);
    };

    return (
      <>
        <RadioGroup
          sx={{ gap: 3, alignItems: 'center' }}
          value={pixCreditOption}
          onChange={(e) => {
            setPixCreditOption(e.target.value);
          }}
        >
          <PaymentPixCreditOptions charge={charge} />
        </RadioGroup>
        <ActionButton
          disabled={!pixCreditOption || isPendingChargeAddPaymentMethod}
          onClick={() => handleSetStep()}
          loading={isPendingChargeAddPaymentMethod}
        >
          {parseInt(pixCreditOption, 19) === 0 ? t('Pay') : t('Continue')}
        </ActionButton>
      </>
    );
  };

  if (isGraphQLMain) {
    return <PaymentActiveCreditCard charge={charge} />;
  }

  return (
    <FormikProvider value={formikbag}>
      <PaymentLinkComment charge={charge} />
      <PaymentActiveCustomer charge={charge} />
      {renderStep()}
      <PaymentLinkPaymentTerm charge={charge} />
      <PaymentLinkTransactionDetails charge={charge} />
      <PaymentLinkAdditionalInfo charge={charge} />
    </FormikProvider>
  );
};

export default PaymentActiveWaitingPaymentMethodPixCreditCard;
